<template>
  <v-card class="mt-3 concept-booth" :lang="lang === 'jp' ? 'ja' : lang" flat>
    <!-- 프리미엄은 카테고리 탭(사진 동그라미, 옆으로 밀기) -->
    <p v-if="isPremium" class="premium-proof">{{ $t("premium_category.proof") }}</p>
    <nav v-if="isPremium" ref="premiumTabs" class="premium-tabs" :class="{ 'is-dragging': tabDrag && tabDrag.moved }" aria-label="카테고리" @pointerdown="startTabDrag" @click.capture="stopClickAfterDrag">
      <button v-for="(item, index) in tabs" :key="item.id" type="button" class="premium-tab" :class="{ 'is-selected': tag === index }" :aria-current="tag === index ? 'page' : null" @click="tag = index">
        <span class="premium-tab-ring" aria-hidden="true">
          <span v-if="item.icons" class="premium-tab-mosaic"><img v-for="src in item.icons" :key="src" :src="src" alt="" draggable="false" /></span>
          <img v-else :src="item.icon" alt="" draggable="false" />
        </span>
        <span class="premium-tab-name">{{ tabLabel(item) }}</span>
      </button>
    </nav>
    <v-sheet v-else class="d-flex justify-space-between align-center px-2">
      <v-slide-group v-model="tag" mandatory show-arrows style="flex: 1; max-width: calc(100% - 80px)">
        <v-slide-item v-for="item in tabs" v-slot="{ active, toggle }" :key="item.id" class="ma-2">
          <v-btn :color="active ? '#d9bddd' : 'white'" class="mx-1 rounded-1" :class="active ? 'active' : 'not-selected'" depressed :elevation="0" @click="toggle" small>{{
            tabLabel(item)
          }}</v-btn>
        </v-slide-item>
      </v-slide-group>

      <!-- 그리드 뷰 토글 -->
      <div class="d-flex ml-2" style="flex-shrink: 0">
        <v-btn icon small @click="gridCols = 2" :color="gridCols === 2 ? 'primary' : 'grey'">
          <v-icon small>mdi-view-grid</v-icon>
        </v-btn>
        <v-btn icon small @click="gridCols = 3" :color="gridCols === 3 ? 'primary' : 'grey'" class="ml-1">
          <v-icon small>mdi-view-module</v-icon>
        </v-btn>
      </div>
    </v-sheet>

    <!-- 다른 화면으로 가는 카테고리(아비크)는 그 화면 카드 하나만 보여 준다. -->
    <v-row v-if="linkTab" no-gutters>
      <v-col :cols="gridCols === 3 ? 4 : 6" class="py-2 pr-1">
        <v-card :to="linkTab.link" flat>
          <v-img :src="linkTab.image" position="top center" :aspect-ratio="2.5 / 3" />
          <v-sheet class="d-flex mt-1 mt-md-2 px-2">
            <span class="px-1 px-sm-2 text-caption text-sm-body-2" style="color: #545454">{{ tabLabel(linkTab) }}</span>
          </v-sheet>
        </v-card>
      </v-col>
    </v-row>
    <v-row v-else-if="list.length" no-gutters>
      <v-col v-for="(item, index) in list" :key="item.id" :cols="gridCols === 3 ? 4 : 6" class="py-2" :class="getColClass(index)">
        <v-card :to="getConceptLink(item.id)" flat class="concept-card">
          <!-- 이미지 -->
          <v-img :lazy-src="require('@/assets/placeholder.jpg')" :src="getThumbnail(item)" position="top center" :aspect-ratio="2.5 / 3">
            <template v-slot:placeholder>
              <v-row class="fill-height ma-0 align-center" justify="center">
                <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
              </v-row>
            </template>
          </v-img>
          <span v-if="isPremium && badges[item.id]" class="concept-badge" :class="badges[item.id] === 'new' ? 'is-new' : 'is-rank r' + badges[item.id]" aria-hidden="true">
            <i v-for="n in 5" :key="n" class="concept-badge-spark" :class="'s' + n"></i>
            <svg v-if="badges[item.id] !== 'new'" class="concept-badge-crown" viewBox="0 0 24 24" fill="none"><path d="M4 19h16M5 16.5 3.5 7l5 4L12 4.5l3.5 6.5 5-4L19 16.5z" stroke="currentColor" stroke-width="2.2" stroke-linejoin="round" /></svg>
            {{ badges[item.id] === "new" ? $t("premium_category.badge_new") : $t("premium_category.badge_rank", { n: badges[item.id] }) }}
          </span>
          <v-sheet class="d-flex flex-column flex-md-row justify-start justify-md-end align-start align-md-center mt-1 mt-md-2 px-2">
            <!-- tag (2열일 때만 표시) -->
            <div v-if="gridCols === 2" class="d-flex flex-wrap mt-1 mb-md-0">
              <div v-for="tag in getTags(item.tag)" :key="tag" class="d-inline-flex">
                <v-chip style="margin-left: 1px; padding-left: 4px; padding-right: 4px" color="grey" :ripple="false" label outlined x-small pill>{{ getConceptTagStr(tag) }}</v-chip>
              </div>
            </div>
            <span class="px-1 px-sm-2 text-caption text-sm-body-2" style="color: #545454; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; max-width: 100%">{{ getTitle(item) }}</span>
            <!-- <span class="pl-1 pl-sm-2 text-caption text-sm-body-2" color="grey">{{ $toComma(item.price) }}원</span> -->
          </v-sheet>
        </v-card>
      </v-col>
    </v-row>
    <v-sheet v-if="!linkTab && !list.length" class="d-flex justify-center align-center font-weight-medium" style="min-height: 100px"> {{ $t("page.conceptbooth.concept_not_exists") }} </v-sheet>
  </v-card>
</template>
<script>
import { AllTag, Tags, PremiumCategories } from "@/constant"

// 프리미엄 카테고리 분류표(컨셉 id → 카테고리). 컨셉 설명에 category가 저장돼 있으면 그것을 먼저 쓴다.
// 커플은 이 표가 아니라 샘플 폴더(couple)로 자동으로 정한다.
import CATEGORY_ASSIGN from "./premiumCategoryAssign.json"
import { mapActions, mapState } from "vuex"

export default {
  props: {
    productType: {
      type: String,
      default: "makershot",
      validator: (value) => ["makershot", "premium"].includes(value),
    },
  },
  data: () => ({
    tags: [AllTag, ...Tags],
    list: [],
    category: "무인촬영",
    tag: 0,
    gridCols: 2, // 2열 또는 3열

    loading: false,
    premiumRank: null, // 컨셉 id → { pos, isNew, hot } (서버가 1시간마다 계산)
    badges: {}, // 지금 목록에서 컨셉 id → "new" 또는 예약 순위(1~3)
    coupleAlbums: null, // 컨셉 id → 커플 샘플 첫 사진(샘플 폴더에 couple이 있는 컨셉만)
    tabDrag: null,
  }),
  computed: {
    ...mapState(["lang"]),
    isPremium() {
      return this.productType === "premium"
    },
    tabs() {
      return this.isPremium ? PremiumCategories : this.tags
    },
    linkTab() {
      const tab = this.isPremium && this.tabs[this.tag]
      return tab && tab.link ? tab : null
    },
  },
  created() {
    if (!this.isPremium) return
    const index = PremiumCategories.findIndex((item) => item.id === this.$route.query.category)
    this.tag = index > 0 ? index : 0
  },
  mounted() {
    this.load()
    this.showSelectedTab()
    if (this.isPremium) {
      window.addEventListener("resize", this.showSelectedTab)
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(this.showSelectedTab)
    }
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.showSelectedTab)
  },
  watch: {
    async category() {
      await this.load()
    },

    async tag() {
      if (this.isPremium) {
        const category = PremiumCategories[this.tag].id
        // 화면을 다시 그리지 않고 주소만 바꿔, 상세에서 돌아와도 고른 탭이 남게 한다.
        this.showSelectedTab(true)
        window.history.replaceState(window.history.state, "", this.$router.resolve({ query: { ...this.$route.query, category } }).href)
      }
      await this.load()
    },

    lang() {
      this.showSelectedTab()
    },

    async productType() {
      await this.load()
    },
  },
  methods: {
    ...mapActions(["loadConcepts"]),
    // 목록 불러오기
    load() {
      // productType을 파라미터로 전달하여 필터링된 데이터 가져오기
      return this.$http
        .post("/api/concept/select", {
          params: {
            productType: this.productType,
          },
        })
        .then(async (res) => {
          if (this.isPremium && PremiumCategories[this.tag].id === "couple") await this.loadCoupleAlbums()
          if (this.isPremium) await this.loadPremiumRank()
          this.list = this.isPremium ? this.arrangePremium(this.filterConceptsByTag(res.data)) : this.filterConceptsByTag(res.data)
        })
        .catch((err) => {
          console.error("Failed to load concepts:", err)
        })
    },

    filterConceptsByTag(concepts) {
      const list = Array.isArray(concepts) ? concepts : []
      const visibleList = list.filter((concept) => concept.status !== "숨김")
      if (this.isPremium) {
        const category = PremiumCategories[this.tag].id
        // 커플은 서버 샘플 폴더에 couple 사진이 있는 컨셉만 자동으로 넣는다.
        if (category === "couple") return visibleList.filter((concept) => this.coupleAlbums && this.coupleAlbums[concept.id])
        if (category === "all") return visibleList
        return visibleList.filter((concept) => this.getPremiumCategories(concept).includes(category))
      }
      const selectedTagIndex = Number(this.tag)

      // 전체 탭과 비정상적인 탭 값은 숨김 항목을 제외한 목록을 보여준다.
      if (!Number.isInteger(selectedTagIndex) || selectedTagIndex <= 0) return visibleList

      const selectedTag = this.tags[selectedTagIndex]
      if (!selectedTag || !selectedTag.title) return visibleList

      return visibleList.filter((concept) => this.getTags(concept.tag).includes(selectedTag.title))
    },

    // 진열 순서·딱지: 서버 순서(신작 → 최근 예약 순)대로 놓고, 신작엔 NEW, 예약 상위 후보 중 앞의 3개에 1~3위.
    async loadPremiumRank() {
      if (this.premiumRank) return
      try {
        const res = await fetch("/api/concept/select/premium-rank")
        if (res.ok) this.premiumRank = Object.fromEntries(((await res.json()).concepts || []).map((item) => [item.id, item]))
      } catch (_) {
        // 실패하면 지금 순서 그대로 보여 준다.
      }
    },

    arrangePremium(list) {
      const rank = this.premiumRank || {}
      const sorted = list
        .map((concept, index) => ({ concept, index }))
        .sort((a, b) => ((rank[a.concept.id] || {}).pos || 9999) - ((rank[b.concept.id] || {}).pos || 9999) || a.index - b.index)
        .map((item) => item.concept)
      const badges = {}
      let place = 0
      sorted.forEach((concept) => {
        const info = rank[concept.id]
        if (!info) return
        if (info.isNew) badges[concept.id] = "new"
        else if (info.hot && place < 3) badges[concept.id] = ++place
      })
      this.badges = badges
      return sorted
    },

    // 서버가 샘플 폴더를 훑어 couple 사진이 있는 컨셉만 한 번에 준다.
    async loadCoupleAlbums() {
      if (this.coupleAlbums) return
      try {
        const res = await fetch("/api/sample-albums/couple-concepts")
        if (res.ok) this.coupleAlbums = (await res.json()).concepts || {}
      } catch (_) {
        // 실패하면 다음에 탭을 열 때 다시 묻는다.
      }
    },

    getThumbnail(item) {
      const couple = this.isPremium && PremiumCategories[this.tag].id === "couple" && this.coupleAlbums && this.coupleAlbums[item.id]
      return couple || "/upload/concept/thumbnail_image/" + item.pattern_1_image
    },

    // 컴퓨터 마우스로 탭 줄을 끌어 옆으로 민다(휴대폰은 손가락으로 원래 밀림).
    startTabDrag(event) {
      if (event.pointerType !== "mouse") return
      const nav = this.$refs.premiumTabs
      this.tabDrag = { x: event.clientX, left: nav.scrollLeft, moved: false }
      const move = (e) => {
        const dx = e.clientX - this.tabDrag.x
        if (Math.abs(dx) > 4) this.tabDrag.moved = true
        nav.scrollLeft = this.tabDrag.left - dx
      }
      const up = () => {
        window.removeEventListener("pointermove", move)
        window.removeEventListener("pointerup", up)
        setTimeout(() => (this.tabDrag = null))
      }
      window.addEventListener("pointermove", move)
      window.addEventListener("pointerup", up)
    },

    stopClickAfterDrag(event) {
      if (this.tabDrag && this.tabDrag.moved) {
        event.preventDefault()
        event.stopPropagation()
      }
    },

    getPremiumCategories(concept) {
      const saved = concept.concept_desc && concept.concept_desc.category
      if (Array.isArray(saved) && saved.length) return saved
      return CATEGORY_ASSIGN[concept.id] || ["princess"]
    },

    // 고른 탭이 화면 밖에 있으면 가운데로 끌어온다.
    showSelectedTab(smooth = false) {
      this.$nextTick(() => {
        const nav = this.$refs.premiumTabs
        const tab = nav && nav.querySelectorAll(".premium-tab")[this.tag]
        if (!tab) return
        nav.scrollTo({ left: tab.offsetLeft - (nav.clientWidth - tab.offsetWidth) / 2, behavior: smooth === true ? "smooth" : "auto" })
      })
    },

    tabLabel(item) {
      return this.isPremium ? this.$t(`premium_category.${item.id}`) : this.$t(`tags.${item.id}`)
    },

    getTags(tagStr) {
      if (!tagStr) {
        return []
      }
      return tagStr
        .split(",")
        .map((tag) => tag.trim())
        .filter(Boolean)
    },

    getConceptTagStr(tag) {
      const tagItem = Tags.find((item) => item.title === tag)
      if (tagItem) return this.$t(`tags.${tagItem.id}`)
      // '공주' 태그는 분위기 태그 목록에 없어 카테고리 이름으로 번역한다.
      return tag === "공주" ? this.$t("premium_category.princess") : tag
    },

    getTitle(item) {
      switch (this.lang) {
        case "en":
          return item.title_en || item.title
        case "jp":
          return item.title_jp || item.title
        default:
          return item.title
      }
    },

    getColClass(index) {
      if (this.gridCols === 3) {
        // 3열일 때
        if (index % 3 === 0) return "pr-1"
        if (index % 3 === 1) return "px-1"
        return "pl-1"
      } else {
        // 2열일 때
        return index % 2 === 0 ? "pr-1" : "pl-1"
      }
    },

    getConceptLink(conceptId) {
      // URL query param의 event 정보를 함께 전달
      const query = {}
      if (this.$route.query.event) {
        query.event = this.$route.query.event
      }

      return {
        path: `/castle/${conceptId}`,
        query: query
      }
    },
  },
}
</script>
<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Noto+Sans+JP:wght@400;700&display=swap");
/* 일본어 화면은 탭·컨셉 이름 모두 Noto Sans JP(라피아 다국어 글꼴 규칙) */
.concept-booth:lang(ja),
.concept-booth:lang(ja) .premium-tab,
.concept-booth:lang(ja) .text-caption,
.concept-booth:lang(ja) .v-btn { font-family: "Noto Sans JP", sans-serif !important; }
/* 목록 맨 위 깨알 문구 */
.premium-proof { margin: -4px 16px 0; text-align: center; font-size: 10.5px; letter-spacing: .02em; color: #b39aa1; }

/* 카드 딱지: 은은한 빛(글로우) + 빛줄기 + 반짝이 */
.concept-card { position: relative; }
.concept-badge { position: absolute; top: 8px; left: 8px; z-index: 1; isolation: isolate; display: inline-flex; align-items: center; gap: 3px; padding: 3px 9px; border-radius: 999px; font-size: 11px; font-weight: 800; line-height: 1.2; letter-spacing: .02em; pointer-events: none; }
.concept-badge::before { content: ""; position: absolute; inset: -3px; z-index: -1; border-radius: inherit; background: var(--glow); filter: blur(7px); opacity: .55; animation: concept-badge-glow 2.6s ease-in-out infinite; }
.concept-badge::after { content: ""; position: absolute; inset: 0; border-radius: inherit; background: linear-gradient(110deg, transparent 35%, rgba(255, 255, 255, .8) 50%, transparent 65%) no-repeat; background-size: 250% 100%; background-position: 150% 0; animation: concept-badge-shine 3.4s ease-in-out infinite; }
.concept-badge.is-rank { color: #fff; text-shadow: 0 1px 2px rgba(90, 20, 40, .35); background: linear-gradient(120deg, #f29ab5, #be4167 60%, #a8325a); --glow: #ff9fbe; --spark: 255, 200, 220; }
.concept-badge.r1 { background: linear-gradient(120deg, #fbe6a6, #e0b24f 45%, #c48a2a); text-shadow: 0 1px 2px rgba(110, 70, 10, .45); --glow: #ffd27a; --spark: 255, 236, 180; }
.concept-badge.is-new { color: #be4167; background: rgba(255, 255, 255, .95); --glow: #ffd1df; --spark: 255, 170, 200; }
.concept-badge-crown { width: 12px; height: 12px; }
.concept-badge-spark { position: absolute; width: 7px; height: 7px; opacity: 0; background: rgb(var(--spark)); clip-path: polygon(50% 0, 62% 38%, 100% 50%, 62% 62%, 50% 100%, 38% 62%, 0 50%, 38% 38%); filter: drop-shadow(0 0 3px rgba(var(--spark), .95)); animation: concept-badge-spark 2.8s ease-in-out infinite; }
.concept-badge-spark.s1 { top: -6px; left: 14%; animation-delay: 0s; }
.concept-badge-spark.s2 { top: -8px; right: 16%; width: 5px; height: 5px; animation-delay: .7s; }
.concept-badge-spark.s3 { bottom: -6px; left: 46%; width: 5px; height: 5px; animation-delay: 1.3s; }
.concept-badge-spark.s4 { top: 30%; right: -8px; animation-delay: 1.9s; }
.concept-badge-spark.s5 { top: 18%; left: -7px; width: 4px; height: 4px; animation-delay: 2.3s; }
@keyframes concept-badge-glow { 0%, 100% { opacity: .35; transform: scale(.96); } 50% { opacity: .85; transform: scale(1.06); } }
@keyframes concept-badge-shine { 0%, 55% { background-position: 150% 0; } 85%, 100% { background-position: -60% 0; } }
@keyframes concept-badge-spark { 0% { opacity: 0; transform: translateY(3px) scale(.2) rotate(0deg); } 30% { opacity: 1; transform: translateY(-1px) scale(1) rotate(45deg); } 65% { opacity: .55; transform: translateY(-5px) scale(.7) rotate(90deg); } 100% { opacity: 0; transform: translateY(-9px) scale(.2) rotate(140deg); } }
@media (prefers-reduced-motion: reduce) {
  .concept-badge::before, .concept-badge::after, .concept-badge-spark { animation: none; }
  .concept-badge-spark { display: none; }
}

/* 카테고리 탭: 사진 동그라미. 화면보다 많으면 옆으로 밀어서 본다. */
.premium-tabs {
  display: flex;
  gap: 14px;
  margin: 8px 0 12px;
  padding: 6px 14px 8px;
  overflow-x: auto;
  scrollbar-width: none;
  user-select: none;
}
.premium-tabs::-webkit-scrollbar { display: none; }
/* 넓은 화면에서 다 들어가면 가운데, 넘치면 왼쪽부터 밀기 */
.premium-tab:first-child { margin-left: auto; }
.premium-tab:last-child { margin-right: auto; }
/* 넓은 화면은 탭 사이를 좁혀 일곱 개가 한 줄에 다 보이게 한다 */
@media (min-width: 600px) {
  .premium-tabs { gap: 8px; }
}
.premium-tab {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 66px;
  padding: 0;
  color: #8a6d61;
  font: inherit;
  background: transparent;
  border: 0;
  cursor: pointer;
}
.premium-tab-ring {
  display: block;
  width: 66px;
  height: 66px;
  padding: 3px;
  border-radius: 50%;
  background: #eadfe2;
  transition: background 200ms ease, transform 160ms cubic-bezier(0.23, 1, 0.32, 1);
}
.premium-tab:active .premium-tab-ring { transform: scale(0.96); }
/* 줄을 끌어 미는 동안에는 눌린 모양을 보이지 않는다 */
.premium-tabs.is-dragging .premium-tab:active .premium-tab-ring { transform: none; }
.premium-tab-ring > img {
  display: block;
  width: 100%;
  height: 100%;
  border: 2px solid #fff;
  border-radius: 50%;
  object-fit: cover;
  object-position: top;
  pointer-events: none;
}
/* 전체: 사진 넷을 한 동그라미에 모은다 */
.premium-tab-mosaic {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border: 2px solid #fff;
  border-radius: 50%;
  background: #fff;
}
.premium-tab-mosaic img {
  display: block;
  width: 100%;
  height: 100%;
  min-height: 0;
  object-fit: cover;
  object-position: top;
  pointer-events: none;
}
.premium-tab-name { margin-top: 6px; font-size: 12px; line-height: 1.3; white-space: nowrap; }
.premium-tab.is-selected { color: #be4167; font-weight: 700; }
.premium-tab.is-selected .premium-tab-ring { background: conic-gradient(from 200deg, #f6b9cb, #be4167, #d39a2f, #f6b9cb); }
.premium-tab:focus-visible { outline: 2px solid #be4167; outline-offset: 3px; border-radius: 12px; }
@media (prefers-reduced-motion: reduce) {
  .premium-tab-ring { transition: none; }
}
::v-deep .v-slide-group__prev,
::v-deep .v-slide-group__next {
  min-width: 20px !important;
}
::v-deep .v-slide-group__content {
  justify-content: center;
}
.v-btn {
  border: 2px solid rgb(223, 223, 223) !important;
}
.active {
  color: black;
}
.not-selected {
  color: rgb(141, 141, 141);
}

.guide {
  position: relative;
  margin-left: auto;
  margin-right: auto;
  width: 96%;
  height: 360px;
  aspect-ratio: 2.4;
  background-image: url("~@/assets/concept/concept_border.png");
  background-size: contain;
}

.guide.small {
  height: 260px !important;
}

.lucid-card {
  background-size: cover;
  background-image: url("~@/assets/concept/lucid-card.png");
  background-size: 90%;
  background-position: 50% 10%;
}

.lucid-card .desc {
  background-color: transparent;
  margin-top: 90px;
}

.lucid-card .desc.small {
  margin-top: 62px !important;
}

.lucid-card.small {
  background-position: 50% 12%;
}
</style>
